// ─────────────────────────────────────────────────────────────────────────────
// Core Entity Types for Sedation App
// ─────────────────────────────────────────────────────────────────────────────

// Import backup types for strong typing
import { CompleteSedationBackup } from './backupTypes';
import type { MedicalHistoryCategory } from '../api/generated/models/MedicalHistoryCategory';
import type { MedicalHistoryField } from '../api/generated/models/MedicalHistoryField';
import type { MedicalHistorySource } from '../api/generated/models/MedicalHistorySource';

// ─────────────────────────────────────────────────────────────────────────────
// Structured medical history (see docs/requirements/MEDICAL_HISTORY_UI_GUIDE.md)
//
// PascalCase deliberately: these ride inside CompleteSedationBackup.Patient and
// the API's JSON binding is case-insensitive, so they land on
// `medicalHistoryFields` unchanged — the same assumption Patient.Title relies on.
// ─────────────────────────────────────────────────────────────────────────────

/** One answer. Identity is (Category, Code) — the server upserts on that pair. */
export interface MedicalHistoryItemEntity {
  /** Server row id. Absent for entries created offline. */
  Id?: string;
  Category: MedicalHistoryCategory;
  /** Catalogued code, or a device-minted `ClinicianNote:<guid>` for free text. */
  Code: string;
  /** Server-resolved display line. May already include the value — see displayLabel(). */
  Label?: string | null;
  Value?: string | null;
  /** false = asked and answered no, or since withdrawn. Never "deleted". */
  IsPresent: boolean;
  Source?: MedicalHistorySource;
  FirstReportedAt?: string | null;
  LastConfirmedAt?: string | null;
  /**
   * Touched on this device. Device-only — stripped before the payload goes up.
   *
   * Load-bearing: everything we send is filed as `source: Clinician`, and a
   * patient pack can never overwrite a Clinician row. Echoing back an untouched
   * PatientPack answer would therefore lock the patient out of ever updating it
   * again. Only edited items are sent.
   *
   * Never cleared after a sync: a 200 does not prove the history landed, and
   * the device's read path returns positives only, so a withdrawn item is
   * absent from the next fetch and would silently reappear.
   */
  LocallyEdited?: boolean;
}

export interface MedicalHistoryFieldEntity {
  Field: MedicalHistoryField;
  Label?: string | null;
  /** Reported PER FIELD — a backfill can reach one field before another. */
  HasStructuredData: boolean;
  Items: MedicalHistoryItemEntity[];
  RenderedText?: string | null;
  LegacyText?: string | null;
}

// Base entity interface for all database records
export interface BaseEntity {
  Id: string;
  CreatedDateTime: string;
  ModifiedDateTime?: string;
  Synced: boolean;
  Deactivated: boolean;
  LastSyncedAt?: string;
  PendingSync?: "create" | "update" | "delete" | null;
}

// Patient entity - core business object
export interface Patient extends BaseEntity {
  Title: string;
  FirstName: string;
  LastName: string;
  /** ISO calendar date, "YYYY-MM-DD". Never a datetime — see normalizeDOBForStorage. */
  DateOfBirth: string | null;
  Sex?: string;
  Age?: number;
  Address?: string | null;
  Town?: string | null;
  Country?: string | null;
  PostCode?: string | null;
  PhoneNumber?: string | null;
  AlternativePhoneNumber?: string | null;
  BusinessName?: string | null;
  Email?: string | null;
  MedicalHistory?: string | null;
  Allergies?: string | null;
  Medications?: string | null;
  AnestheticHistory?: string | null;
  /** Structured history. Undefined on patients that predate the backfill. */
  MedicalHistoryFields?: MedicalHistoryFieldEntity[] | null;
  ASAClassification?: number;
  SmokingStatus?: string;
  AlcoholStatus?: string;
  LastMealAgoInHours?: number | null;
  LastFluidAgoInHours?: number | null;
  Occupation?: string | null;
  HeightFormat?: string;
  Height?: number | null;
  WeightFormat?: string;
  Weight?: number | null;
  TicketId?: string | null;
  Notes?: string | null;
  SmokingNote?: string | null;
  AlcoholNote?: string | null;
  AirwayDifficultyAnticipated?: boolean | null; // S-16: null = not yet assessed
  AirwayNote?: string | null;
  RecreationalDrugUse?: boolean | null; // S-19: from the patient pack; null = not asked
  RecreationalDrugNote?: string | null;
  Vapes?: boolean | null; // S-19: no amount recorded
  BMI?: number;
  UniqueId: string;
}

// UI-friendly patient data (transformed for display)
export interface PatientUI {
  Date: string;
  Title: string;
  FullName: string;
  DOB: string;
  Age: number;
  BMI: number;
  UniqueId: string;
  Email: string;
  Cell: string;
  Action: string;
  _original: Patient; // Keep reference to original data
}

// Sedation detail entity
export interface SedationDetail extends BaseEntity {
  PatientId: string;
  Status: "pre" | "intra" | "post" | "completed";
  StartTime?: string;
  EndTime?: string;
  Notes?: string;
  // Add sedation-specific fields
}

// Aldrete Score structure
export interface AldreteScore {
  Activity: number;
  Respiration: number;
  Circulation: number;
  Consciousness: number;
  Oxygenation: number;
  TotalScore: number;
  Timestamp: string;
}

// API response wrapper for paginated data
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Extended paginated response with data source info
export interface ServiceResponse<T> extends PaginatedResponse<T> {
  source: "api" | "local";
}

// Network connectivity state
export interface NetworkState {
  online: boolean;
  checking: boolean;
  lastChecked: Date | null;
  retryCount: number;
}

// Repository operation options
export interface RepositoryOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  includeDeactivated?: boolean;
  forceOffline?: boolean;
}

// Database configuration types
export interface DatabaseConfig {
  name: string;
  version: number;
  stores: DatabaseStoreConfig[];
}

export interface DatabaseStoreConfig {
  name: string;
  keyPath: string;
  indexes: DatabaseIndexConfig[];
}

export interface DatabaseIndexConfig {
  name: string;
  keyPath: string | string[];
  options?: IDBIndexParameters;
}

// Database stores enumeration
export enum DatabaseStore {
  PATIENTS = "patients",
  SEDATION_DETAILS = "sedationDetails",
  SEDATION_SESSIONS = "sedationSessions",
  SEDATION_REPORTS = "sedationReports",
  SEDATION_IMAGES = "sedation_images",
  MEASUREMENTS = "measurements",
  OUTBOX = "outbox",
  META = "meta",
  BACKUP_METADATA = "backupMetadata",
  CPD_FLAGS = "cpdFlags",
}

// Outbox record for sync operations - now with generic typing
export interface OutboxRecord<TData = any> {
  id: string;
  entityType: string;
  entityId: string;
  operation: "create" | "update" | "delete" | "sedation_completed" | "email_send";
  data: TData;
  enqueuedAt: string;
  attempts: number;
  lastAttemptAt?: string;
  errors?: string[];
}

// Specific outbox record types for different operations
export interface SedationCompletionOutboxRecord extends OutboxRecord<SedationCompletionPayload> {
  operation: "sedation_completed";
  entityType: "SedationCompletion";
}

// Email outbox record type for offline email sending
export interface EmailOutboxRecord extends OutboxRecord<EmailOutboxPayload> {
  operation: "email_send";
  entityType: "Email";
}

// Email payload interface for outbox
export interface EmailOutboxPayload {
  emailMessage: {
    To: string[];
    Subject: string;
    Body: string;
    IsHtml: boolean;
    Attachments?: {
      Base64: string;
      FileName: string;
      ContentType: string;
    }[];
  };
  patientId: string;
  sedationId: string;
  retryCount: number;
}

// Strongly typed payload for sedation completion events
export interface SedationCompletionPayload {
  eventType: "SEDATION_COMPLETED";
  patientId: string;
  sedationId: string;
  timestamp: string;
  data: CompleteSedationBackup; // Use the existing complete backup structure
  metadata: SedationEventMetadata;
}

// Event metadata for tracking
export interface SedationEventMetadata {
  source: "electron_sedation_app";
  version: string;
  aggregatedAt: string;
  backupError?: string;
  partialBackup?: boolean;
}

// Meta record for key-value storage
export interface MetaRecord {
  key: string;
  value: any;
  updatedAt: string;
}

// Database transaction types
export interface DBTransaction extends IDBTransaction {
  // Extended properties if needed
}

export interface DBStore extends IDBObjectStore {
  // Extended properties if needed
}

// Error types for better error handling
export interface AppError extends Error {
  code: string;
  context?: Record<string, any>;
  timestamp: string;
}

// API endpoint configuration
export interface APIConfig {
  baseUrl: string;
  timeout: number;
  endpoints: {
    patients: string;
    patient: (id: string) => string;
    sedationDetails: string;
    sedationDetail: (id: string) => string;
  };
}

// Form field types for React components
export interface FormField {
  name: string;
  label: string;
  type: "text" | "email" | "tel" | "date" | "number" | "select";
  required: boolean;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
}

// Statistics for dashboard/summary views
export interface PatientStats {
  total: number;
  recent: number;
  filtered: number;
  searchActive: boolean;
}

// Search suggestion for autocomplete
export interface SearchSuggestion {
  id: string;
  label: string;
  email?: string;
  phone?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sedation Report Types (Matching C# Backend Model)
// ─────────────────────────────────────────────────────────────────────────────

// Recovery Type enum - matches C# RecoveryType enum
export enum RecoveryType {
  Spontaneous = "Spontaneous",
  Reversal = "Reversal"
}

// Sedation Report Receipt entity - matches C# SedationReportReceiptDto exactly
export interface SedationReportReceipt extends BaseEntity {
  // Core identification
  SedationDetailId: string; // Guid - foreign key to SedationDetail
  
  // Recovery details
  RecoveryType: RecoveryType | null; // RecoveryType enum
  SuitableForFutureSedation: boolean; // bool
  ReasonNotSuitableForFutureSedation: string | null; // string?
  AldreteScore: number | null; // decimal?
  DurationOfMonitoredRecovery: number | null; // decimal? - in minutes
  
  // Clinical management
  Comments: string | null; // string?
  SignedBy: string | null; // string?
  
  // Patient guidance
  VerbalAdviceGiven: boolean; // bool
  WrittenAdviceGiven: boolean; // bool
  Escort: string | null; // string?
  InstructionsGivenToEscort: boolean; // bool
  
  // Medical procedures
  IVCannulaRemoved: boolean; // bool
  ReasonIVCannulaNotRemoved: string | null; // string?
  
  // Timing fields (combined time fields)
  SedationCompletedTime: string | null; // string? - HH:MM format
  DischargeTime: string | null; // string? - HH:MM format
  
  // Administrative
  ReceiptEmails: string | null; // string? - additional email addresses
  DefaultEmailRecipients: string | null; // string? - default email address
  ExcludeDefaultRecipients: boolean; // bool - whether to exclude default email
  AdditionalNotes: string | null; // string?
  AdditionalNotesRecordedAt: string | null; // string? - timestamp when follow-up notes were saved
  AdditionalNotesRecordedBy: string | null; // string? - user who saved the follow-up notes
  
  // TROOP Outcome
  ShouldGenerateTroopOutcome: boolean; // bool - whether to generate TROOP outcome
  TroopOutcomeId: string | null; // Guid? - ID of the TROOP outcome
}

// UI-friendly sedation report data (used in PostSedationEmbedded form)
export interface SedationReportUI {
  // Recovery details
  recoveryType: string; // RecoveryType as string for form select
  suitableForFutureSedation: boolean;
  reasonNotSuitableForFutureSedation: string;
  aldreteScore: string; // string for form input, converted to number for API
  durationOfMonitoredRecovery: string; // string for form input, converted to number for API
  
  // Clinical management
  comments: string;
  signedBy: string;
  
  // Patient guidance
  verbalAdviceGiven: boolean;
  writtenAdviceGiven: boolean;
  escort: string;
  instructionsGivenToEscort: boolean;
  
  // Medical procedures
  ivCannulaRemoved: boolean;
  reasonIVCannulaNotRemoved: string;
  
  // Timing fields (as combined time strings)
  sedationCompletedTime: string; // HH:MM format
  dischargeTime: string; // HH:MM format
  
  // Administrative
  receiptEmails: string;
  defaultEmailRecipients: string;
  excludeDefaultRecipients: boolean;
  additionalNotes: string;
  additionalNotesRecordedAt: string | null;
  additionalNotesRecordedBy: string | null;
  
  // TROOP Outcome
  shouldGenerateTroopOutcome: boolean;
  troopOutcomeId: string | null;
}

// Conversion utilities for SedationReport
export class SedationReportConverter {
  /**
   * Converts UI form data to backend entity format
   */
  static fromUI(uiData: SedationReportUI, sedationDetailId: string): Omit<SedationReportReceipt, keyof BaseEntity> {
    return {
      SedationDetailId: sedationDetailId,
      RecoveryType: uiData.recoveryType ? (uiData.recoveryType as RecoveryType) : null,
      SuitableForFutureSedation: uiData.suitableForFutureSedation,
      ReasonNotSuitableForFutureSedation: uiData.reasonNotSuitableForFutureSedation || null,
      AldreteScore: uiData.aldreteScore ? parseFloat(uiData.aldreteScore) : null,
      DurationOfMonitoredRecovery: uiData.durationOfMonitoredRecovery ? parseFloat(uiData.durationOfMonitoredRecovery) : null,
      Comments: uiData.comments || null,
      SignedBy: uiData.signedBy || null,
      VerbalAdviceGiven: uiData.verbalAdviceGiven,
      WrittenAdviceGiven: uiData.writtenAdviceGiven,
      Escort: uiData.escort || null,
      InstructionsGivenToEscort: uiData.instructionsGivenToEscort,
      IVCannulaRemoved: uiData.ivCannulaRemoved,
      ReasonIVCannulaNotRemoved: uiData.reasonIVCannulaNotRemoved || null,
      SedationCompletedTime: uiData.sedationCompletedTime || null,
      DischargeTime: uiData.dischargeTime || null,
      ReceiptEmails: uiData.receiptEmails || null,
      DefaultEmailRecipients: uiData.defaultEmailRecipients || null,
      ExcludeDefaultRecipients: uiData.excludeDefaultRecipients,
      AdditionalNotes: uiData.additionalNotes || null,
      AdditionalNotesRecordedAt: uiData.additionalNotesRecordedAt || null,
      AdditionalNotesRecordedBy: uiData.additionalNotesRecordedBy || null,
      // TROOP Outcome
      ShouldGenerateTroopOutcome: uiData.shouldGenerateTroopOutcome,
      TroopOutcomeId: uiData.troopOutcomeId || null,
    };
  }

  /**
   * Converts backend entity to UI form data format
   */
  static toUI(entity: SedationReportReceipt): SedationReportUI {
    return {
      recoveryType: entity.RecoveryType || '',
      suitableForFutureSedation: entity.SuitableForFutureSedation,
      reasonNotSuitableForFutureSedation: entity.ReasonNotSuitableForFutureSedation || '',
      // Default to 10 (matches createEmptyUI) when no score has been recorded yet — the
      // sedationist can still change it; only an explicit value (including 0) is preserved as-is.
      aldreteScore: entity.AldreteScore != null ? entity.AldreteScore.toString() : '10',
      durationOfMonitoredRecovery: entity.DurationOfMonitoredRecovery?.toString() || '',
      comments: entity.Comments || '',
      signedBy: entity.SignedBy || '',
      verbalAdviceGiven: entity.VerbalAdviceGiven,
      writtenAdviceGiven: entity.WrittenAdviceGiven,
      escort: entity.Escort || '',
      instructionsGivenToEscort: entity.InstructionsGivenToEscort,
      ivCannulaRemoved: entity.IVCannulaRemoved,
      reasonIVCannulaNotRemoved: entity.ReasonIVCannulaNotRemoved || '',
      sedationCompletedTime: entity.SedationCompletedTime || '',
      dischargeTime: entity.DischargeTime || '',
      receiptEmails: entity.ReceiptEmails || '',
      defaultEmailRecipients: entity.DefaultEmailRecipients || '',
      excludeDefaultRecipients: entity.ExcludeDefaultRecipients,
      additionalNotes: entity.AdditionalNotes || '',
      additionalNotesRecordedAt: entity.AdditionalNotesRecordedAt || null,
      additionalNotesRecordedBy: entity.AdditionalNotesRecordedBy || null,
      // TROOP Outcome
      shouldGenerateTroopOutcome: entity.ShouldGenerateTroopOutcome,
      troopOutcomeId: entity.TroopOutcomeId || null,
    };
  }

  /**
   * Creates empty UI form data with default values
   */
  static createEmptyUI(): SedationReportUI {
    return {
      recoveryType: '',
      suitableForFutureSedation: false,
      reasonNotSuitableForFutureSedation: '',
      aldreteScore: '10',
      durationOfMonitoredRecovery: '',
      comments: '',
      signedBy: '', // Will be populated from auth store
      verbalAdviceGiven: false,
      writtenAdviceGiven: false,
      escort: '',
      instructionsGivenToEscort: false,
      ivCannulaRemoved: false,
      reasonIVCannulaNotRemoved: '',
      sedationCompletedTime: '',
      dischargeTime: '',
      receiptEmails: '',
      defaultEmailRecipients: 'reports@sedationsolutions.co.uk',
      excludeDefaultRecipients: false,
      additionalNotes: '',
      additionalNotesRecordedAt: null,
      additionalNotesRecordedBy: null,
      // TROOP Outcome
      shouldGenerateTroopOutcome: false,
      troopOutcomeId: null,
    };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Measurement Types (Matching C# Backend Model)
// ─────────────────────────────────────────────────────────────────────────────

// Main sedation measurement entity - matches C# SedationMeasurement class
export interface SedationMeasurement extends BaseEntity {
  // Foreign Key
  SedationDetailId: string; // Maps to Guid SedationDetailId in C#
  
  // Timing
  RecoveryTime: number | null; // int? RecoveryTime in C#
  
  // Vital Signs (all decimal? in C#)
  OxygenSaturation: number | null; // decimal? OxygenSaturation - SpO2 percentage
  PulseRate: number | null; // decimal? PulseRate - BPM
  BloodPressureSystolic: number | null; // decimal? BloodPressureSystolic
  BloodPressureDiastolic: number | null; // decimal? BloodPressureDiastolic
  EtCO2: number | null; // decimal? EtCO2 - End-tidal CO2 in mmHg
  RespiratoryRate: number | null; // decimal? RespiratoryRate - Breaths per minute
  
  // Assessment (boolean fields in C#)
  NauseaAndVomiting: boolean; // bool NauseaAndVomiting
  Pain: boolean; // bool Pain
  
  // Clinical Notes (string? in C#)
  AdverseEvents: string | null; // string? AdverseEvents
  AdditionalNotes: string | null; // string? AdditionalNotes
}

// UI-friendly measurement data (used in PostSedationEmbedded)
export interface MeasurementUI {
  id: string;
  recoveryTime: string;
  oxygenSaturation: string;
  pulseRate: string;
  bloodPressureSystolic: string;
  bloodPressureDiastolic: string;
  etco2: string;
  respiratoryRate: string;
  nauseaAndVomiting: boolean;
  pain: boolean;
  adverseEvents: string;
  additionalNotes: string;
  timestamp: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Complete Backup Types - Export backup-related types
// ─────────────────────────────────────────────────────────────────────────────

export * from './backupTypes';
